/**
 * Credit economics — the single conversion between USD model spend and Brew credits.
 *
 * Why anchor credits to USD?
 *   - Models cost different amounts per token (e.g. Sonnet ~1.67× cheaper
 *     than Opus per token at the gateway-billed rate, larger gaps at the
 *     family level when image / reasoning models enter the mix).
 *   - A flat tokens→credits ratio (the legacy `tokensToCredits`) silently subsidized expensive models
 *     and overcharged cheap ones. Anchoring to dollars is provider-agnostic and audit-friendly.
 *
 * Source of truth:
 *   - Per-model prices live in `lib/billing/model-pricing.ts`.
 *   - `lib/billing/cost.ts` turns token/image counts into a USD breakdown.
 *   - This file converts that USD figure to credits and back.
 *
 * Rounding rule (important):
 *   - We deliberately do NOT round up at the per-event level, because tiny events would
 *     multiply round-up bias massively across 1000s of small calls.
 *   - Instead, every event stores the exact fractional credit value (`costUsd / USD_PER_CREDIT`).
 *   - The period total is rounded UP exactly once when read or aggregated
 *     (see `costUsdToDisplayCredits` and the period aggregation in `convex/billing/queries.ts`).
 */

/** 1 credit = $0.01 of underlying gateway model spend (100 credits = $1). */
export const USD_PER_CREDIT = 0.01

/**
 * Convert USD spend to fractional credits. Use this on ledger rows so we can
 * sum many small events without per-event rounding bias.
 */
export function costUsdToCreditsExact(costUsd: number): number {
  if (!Number.isFinite(costUsd) || costUsd <= 0) {
    return 0
  }
  return costUsd / USD_PER_CREDIT
}

/**
 * A float-noise guard for the display ceil. IEEE-754 makes some exact-integer
 * credit totals land a hair ABOVE the integer (e.g. `0.14 / 0.01 =
 * 14.000000000000002`), which a naive `Math.ceil` rounds to 15 — a silent
 * 1-credit OVERCHARGE. This epsilon is ~5 orders of magnitude below any real
 * credit fraction (the cheapest gateway charge is far larger) and ~5 above the
 * float error at these magnitudes, so it snaps ONLY the representation noise and
 * never under-counts genuine fractional usage.
 */
const CREDIT_CEIL_EPSILON = 1e-9

/**
 * Convert USD spend to displayable integer credits.
 *
 * Use this for any user-facing total (period summary, plan progress, the chat
 * sidebar) AND the period enforcement total. Always rounds UP so a fractional
 * credit consumed shows as a full credit consumed — i.e. we never under-count
 * usage — while snapping IEEE-754 noise so an exact integer total never ceils up
 * by one (see {@link CREDIT_CEIL_EPSILON}).
 */
export function costUsdToDisplayCredits(costUsd: number): number {
  return Math.ceil(costUsdToCreditsExact(costUsd) - CREDIT_CEIL_EPSILON)
}

/** Convert credits back to USD spend. Exact, no rounding. */
export function creditsToCostUsd(credits: number): number {
  if (!Number.isFinite(credits) || credits <= 0) {
    return 0
  }
  return credits * USD_PER_CREDIT
}
